import {
  AppBar,
  Stack,
  Paper,
  IconButton,
  Button,
  Zoom,
  Tabs,
  Tab,
  Typography,
  useMediaQuery, Dialog, DialogContent, DialogContentText, DialogActions, DialogTitle
} from '@mui/material';
import {
  useState,
  useContext,
  useCallback,
  useMemo,
  useEffect,
  createContext,
  Dispatch,
  SetStateAction,
  PropsWithChildren,
  useDeferredValue
} from 'react';
import {Link, Routes, Route, useNavigate, useLocation} from 'react-router-dom';
import axios from 'axios';
import logo from './assets/logo.svg';
import {parseJwt} from './utils/jwt';
import {Answer} from './components/answer';
import {LoginForm} from './components/auth/login-form';
import {PasswordForm, SetPassword} from './components/auth/password-form';
import {Register} from './components/auth/register';
import {LicenseRejected} from './components/auth/license-rejected';
import {LicenseAgreement} from './components/auth/license-agreement';
import {AuthProvider, AuthContext} from './context/auth.context';
import {Create} from './components/create';
import {Logout} from '@mui/icons-material';
import {Role, Users} from './components/admin/users';
import {DialogProvider, DialogType, useSource} from 'avrora';
import {ErrorPage, Confirmed} from './components/static';
import {Domains} from './components/admin/domains';
import {Config} from './components/admin/config';
import {License} from "./components/admin/license";

export type Configuration = Record<string, any>;

export const ConfigurationContext = createContext<[Configuration, Dispatch<SetStateAction<Configuration>>]>([{}, () => void 0]);

export const AuthStepContext = createContext<{ resetStep: () => void } | undefined>(undefined);

export const ConfigurationProvider = ({children, isPublic}: PropsWithChildren<{
  isPublic?: boolean
}>) => {
  const {
    data: configuration,
    fetch
  } = useSource<Configuration>(() => axios.get(isPublic ? '/ecfg/public/application-info' : '/ecfg/api/application-info?public=false').then(resp => resp.data), []);

  const setConfiguration = useCallback<Dispatch<SetStateAction<Configuration>>>(action => {
    if (action instanceof Function) {
      action = action(configuration ?? {});
    }

    axios.post(`/ecfg/api/application-info?public=${isPublic ? 'true' : 'false'}`, action).then(fetch);
  }, [configuration, fetch, isPublic]);

  const contextBody = useMemo<[Configuration, Dispatch<SetStateAction<Configuration>>]>(() => [configuration ?? {}, setConfiguration], [configuration, setConfiguration]);
  const context = useDeferredValue(contextBody);

  return (
      <ConfigurationContext.Provider value={context}>
        {children}
      </ConfigurationContext.Provider>
  )
}

function App() {
  return (
      <ConfigurationProvider isPublic>
        <DialogProvider
            Render={({open, loading, onClose, form, title, type, text, submitEnabled}) => (
                <Dialog disablePortal TransitionComponent={Zoom} open={open}>
                  <DialogTitle>{title}</DialogTitle>

                  <DialogContent>
                    <DialogContentText>{text}</DialogContentText>

                    {form}
                  </DialogContent>

                  <DialogActions>
                    <Button type="button" onClick={onClose}>Закрыть</Button>

                    {type && [DialogType.Form, DialogType.Confirm].includes(type) && (
                        <Button type="submit" disabled={!submitEnabled || loading}>ОК</Button>
                    )}
                  </DialogActions>
                </Dialog>
            )}>
          <AuthProvider>
            <Root/>
          </AuthProvider>
        </DialogProvider>
      </ConfigurationProvider>
  );
}

const Header = () => {
  const {user, logout} = useContext(AuthContext);
  const navigate = useNavigate();
  const authStepContext = useContext(AuthStepContext);

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!user && authStepContext) {
      authStepContext.resetStep();
      navigate('/');
    } else {
      navigate('/');
    }
  };

  return (
      <AppBar sx={{backgroundColor: "#fff", boxShadow: "none"}}>
        <Stack width="100%" px={2} sx={{margin: "0 auto", flexGrow: 1}} direction="row"
               alignItems="center"
               justifyContent="space-between">
          <a href="/" onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
            <img src={logo} alt="logo" style={{width: "64px", height: "64px"}}/>
          </a>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography>{user?.sub}</Typography>

            {user && (
                <IconButton onClick={() => {
                  logout();
                  localStorage.removeItem('emailFormData')
                  window.location.href = "/eus/logout";
                  // navigate('/');
                }}>
                  <Logout/>
                </IconButton>
            )}
          </Stack>
        </Stack>
      </AppBar>
  )
}

const Root = () => {
  const [{username, password, secretCode}, setCredentials] = useState({
    username: '',
    password: '',
    secretCode: ''
  });
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  const {pathname} = useLocation();

  const [register, setRegister] = useState(false);
  const [twoFA, setTwoFA] = useState(false);
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [showLicenseAgreement, setShowLicenseAgreement] = useState(false);
  const [licenseRejected, setLicenseRejected] = useState(false);
  const [tempAccessToken, setTempAccessToken] = useState<string | null>(null);

  const {user, login, reset2FA} = useContext(AuthContext);

  const [error, setError] = useState<string>();
  const [errorSecretCode, setErrorSecretCode] = useState<string>();
  const [no2FARegistration, setNo2FARegistration] = useState(false);
  const [eus002Error, setEus002Error] = useState(false);

  const handleLogin = useCallback(() => {
    if (twoFAEnabled && secretCode) {
      if (!tempAccessToken) {
        setErrorSecretCode("Ошибка: токен не найден. Попробуйте снова.");
        setCredentials(old => ({...old, secretCode: ''}));
        return;
      }
      login(username, password, secretCode, tempAccessToken).then(() => {
        setStep(0);
        setCredentials({username: '', password: '', secretCode: ''});
        setTwoFAEnabled(false);
        setTempAccessToken(null);
        setError(undefined);
        setErrorSecretCode(undefined);
        navigate('/');
      }).catch(e => {
        if (e.response?.status === 401) {
          setErrorSecretCode("Неверный код подтверждения");
          setCredentials(old => ({...old, secretCode: ''}));
        } else if (e.response?.status === 409 &&
            e.response?.data?.message !== undefined &&
            e.response.data.message.indexOf("verification code") !== -1) {
          setErrorSecretCode(e.response.data.message)
          setCredentials(old => ({...old, secretCode: ''}));
        } else {
          setErrorSecretCode("Ошибка авторизации");
          setCredentials(old => ({...old, secretCode: ''}));
        }
      });
    } else {
      login(username, password, secretCode).then(() => {
        setStep(0);
        setCredentials({username: '', password: '', secretCode: ''});
        setTwoFAEnabled(false);
        setTempAccessToken(null);
        setError(undefined);
        setErrorSecretCode(undefined);
        navigate('/');
      }).catch(e => {
        if (e.response?.status === 401) {
          if (step === 2) {
            setErrorSecretCode("Неверный код подтверждения");
            setCredentials(old => ({...old, secretCode: ''}));
          } else {
            setError("Неверный пароль");
          }
        } else if (e.response?.status === 409 &&
            e.response?.data?.message !== undefined &&
            e.response.data.message.indexOf("verification code") !== -1) {
          setErrorSecretCode(e.response.data.message)
          setCredentials(old => ({...old, secretCode: ''}));
        } else {
          setErrorSecretCode("Ошибка авторизации");
          setCredentials(old => ({...old, secretCode: ''}));
        }
      });
    }
  }, [login, username, password, secretCode, step, twoFAEnabled, tempAccessToken, navigate]);

  const handlePasswordSubmit = useCallback(() => {
    const base64 = btoa(`${username}:${password}`);
    axios.post('/eus/auth/basic', null, {
      headers: {
        Authorization: `Basic ${base64}`,
        Accept: "application/json"
      }
    }).then((resp: any) => {
      const accessToken = resp.data?.auth || resp.data?.access_token || resp.data?.accessToken;
      if (accessToken) {
        const tokenData = parseJwt(accessToken);
        const tokenRoles = tokenData?.roles;
        const rolesArray = Array.isArray(tokenRoles) ? tokenRoles : (tokenRoles ? [tokenRoles] : []);
        const hasTwoFA = rolesArray.includes("twofa");
        const hasUser = rolesArray.includes("user") || rolesArray.includes("USER");
        
        if (hasTwoFA) {
          setTempAccessToken(accessToken);
          setTwoFAEnabled(true);
          setStep(2);
          setError(undefined);
          setErrorSecretCode(undefined);
          setCredentials(old => ({...old, secretCode: ''}));
        } else if (hasUser) {
          login(username, password, "").then(() => {
            setStep(0);
            setCredentials({username: '', password: '', secretCode: ''});
            setTwoFAEnabled(false);
            setTempAccessToken(null);
            setError(undefined);
            setErrorSecretCode(undefined);
            navigate('/');
          }).catch(e => {
            if (e.response?.status === 401) {
              setError("Неверный пароль");
            } else {
              setError("Ошибка авторизации");
            }
          });
        } else {
          setError("Ошибка авторизации");
        }
      } else {
        setError("Ошибка авторизации");
      }
    }).catch(e => {
      const message = e.response?.data?.message || '';
      if (e.response?.status === 401) {
        if (message.indexOf('[EUS-001]') !== -1) {
          setError("Неверный пароль");
          setEus002Error(false);
        } else if (message.indexOf('[EUS-002]') !== -1) {
          setNo2FARegistration(true);
          setEus002Error(true);
          setError(undefined);
          setErrorSecretCode("Нет регистрации 2FA");
        } else {
          setError("Неверный пароль");
          setEus002Error(false);
        }
      } else if (e.response?.status === 409 ||
          (e.response?.data?.message !== undefined &&
          (e.response.data.message.indexOf("verification code") !== -1 || 
           e.response.data.message.indexOf("2FA") !== -1))) {
        setStep(2);
        setError(undefined);
        setErrorSecretCode(undefined);
        setCredentials(old => ({...old, secretCode: ''}));
      } else {
        setError("Ошибка авторизации");
      }
    });
  }, [username, password, login, navigate]);

  const handleShowLicenseAgreement = useCallback(() => {
    setShowLicenseAgreement(true);
  }, []);

  const handleAcceptLicense = useCallback(() => {
    setShowLicenseAgreement(false);
    setRegister(true);
  }, []);

  const handleRejectLicense = useCallback(() => {
    setShowLicenseAgreement(false);
    setLicenseRejected(true);
  }, []);

  const handleGoHome = useCallback(() => {
    setLicenseRejected(false);
    setRegister(false);
    setShowLicenseAgreement(false);
    setStep(0);
    setCredentials({username: '', password: '', secretCode: ''});
    setError(undefined);
    setErrorSecretCode(undefined);
    setTwoFAEnabled(false);
    setTempAccessToken(null);
  }, []);

  const resetStep = useCallback(() => {
    setStep(0);
    setCredentials({username: '', password: '', secretCode: ''});
    setError(undefined);
    setErrorSecretCode(undefined);
    setTwoFAEnabled(false);
    setTempAccessToken(null);
    setNo2FARegistration(false);
    setEus002Error(false);
  }, []);

  // return <Registered />;
  const roles = useMemo<string[] | undefined>(() => user?.roles, [user]);

  useEffect(() => {
    if (roles && !roles.includes(Role.User)) {
      navigate('/page/users');
    }
  }, [roles]);

  const location = useLocation();

  const parts = location.pathname?.split('/');

  const isMobile = useMediaQuery('(max-width:500px)');

  return (
    <AuthStepContext.Provider value={{ resetStep }}>
      <Header/>

      <Stack alignItems="center" paddingTop="140px" paddingBottom="160px" spacing={2}>
        {user ? (
          <>
            {roles && roles.length > 1 && (
              <Tabs
                  value={pathname.substring(6)}
                  onChange={(_e, value) => {
                    navigate(value ? "/page/" + value : "/");
                  }}
                  variant={isMobile ? "scrollable" : "standard"}
                  scrollButtons={isMobile ? "auto" : false}
                  allowScrollButtonsMobile
                  sx={{
                    width: '100%',
                    maxWidth: '100vw',
                    px: isMobile ? 1 : 0,
                    bgcolor: 'transparent',
                    overflowX: 'hidden',
                    '& .MuiTabs-scroller': {
                      overflowX: isMobile ? 'auto' : 'visible',
                      overflowY: 'hidden',
                    },
                    '& .MuiTab-root': {
                      minWidth: isMobile ? 120 : undefined,
                      fontSize: isMobile ? '0.8125rem' : '0.875rem',
                      textTransform: 'none',
                      maxWidth: isMobile ? 'none' : undefined,
                    },
                    '& .MuiTabs-flexContainer': {
                      justifyContent: isMobile ? 'flex-start' : 'center',
                      flexWrap: 'nowrap',
                    }
                  }}
              >
                {roles.includes(Role.User) && (
                    <Tab value="" label="Написать"/>
                )}

                {(roles.includes(Role.Admin) || roles.includes(Role.UserAdmin)) && (
                    <Tab value="users" label="Пользователи"/>
                )}

                {roles.includes(Role.Admin) && (
                    <Tab value="config" label="Конфигурация"/>
                )}

                {roles.includes(Role.Admin) && (
                    <Tab value="domains" label="Домены"/>
                )}

                {roles.includes(Role.Admin) && (
                    <Tab value="domains/external" label="Настройка внешних пользователей"/>
                )}

                {roles.includes(Role.Admin) && (
                    <Tab value="license" label="Лицензия"/>
                )}
              </Tabs>
          )}

          <Paper sx={{margin: "0 auto", maxWidth: isMobile ? "100vw" : "100%", overflowX: "hidden", px: isMobile ? "12px" : 0}}>
            <Routes>
              {roles?.includes(Role.User) && (
                  <Route index element={<Create/>}/>
              )}

              {roles?.includes(Role.User) && (
                  <Route path="post">
                    <Route index element={<Answer/>}/>
                  </Route>
              )}

              {(roles?.includes(Role.Admin) || roles?.includes(Role.UserAdmin)) && (
                  <>
                    <Route path="page/users/:id?" Component={Users}/>
                  </>
              )}

              {roles?.includes(Role.Admin) && (
                  <>
                    <Route path="page/config" Component={Config}/>
                  </>
              )}

              {roles?.includes(Role.Admin) && (
                  <><Route path="page/license" Component={License}/></>
              )}

              {roles?.includes(Role.Admin) && (
                  <>
                    <Route path="page/domains/:id?" Component={Domains}/>
                  </>
              )}

              <Route path="page/error" Component={ErrorPage}/>
            </Routes>
          </Paper>
          </>
        ) : (
          <>
            {showLicenseAgreement && (
              <Paper sx={{margin: "0 auto"}}>
                <LicenseAgreement onAccept={handleAcceptLicense} onReject={handleRejectLicense}/>
              </Paper>
            )}

            {licenseRejected && (
              <Paper sx={{margin: "0 auto"}}>
                <LicenseRejected onGoHome={handleGoHome}/>
              </Paper>
            )}

            {register && (
              <Paper sx={{margin: "0 auto"}}>
                <Register email={username}/>
              </Paper>
            )}

            {parts[2] === 'confirmed' && (
              <Paper sx={{margin: "0 auto"}}>
                <Confirmed email={username}/>
              </Paper>
            )}

            {parts[2] === 'notfound' && (
              <Paper sx={{margin: "0 auto"}}>
                <Confirmed email={username}/>
              </Paper>
            )}

            {parts[2] === 'password' && (
              <Paper sx={{margin: "0 auto"}}>
                <SetPassword id={parts[3]}/>
              </Paper>
            )}

            {!showLicenseAgreement && !licenseRejected && !register && parts[2] !== 'confirmed' && parts[2] !== 'notfound' && parts[2] !== 'password' && (
              <Paper sx={{margin: "0 auto"}}>
        {step === 0 && (
            <LoginForm
                error={error}
                value={username}
                onRegister={() => username && handleShowLicenseAgreement()}
                onChange={username => {
                  setError(undefined);
                  setCredentials(old => ({...old, username}))
                }}
                onSecretCodeChange={() => {}}
                onSubmit={() => setStep(old => old + 1)}
            />
        )}

        {step === 1 && (
            <PasswordForm
                error={error}
                errorSecretCode={eus002Error ? "Нет регистрации 2FA" : undefined}
                value={password}
                email={username}
                twoFA={false}
                secretCode=""
                onChange={password => {
                  setError(undefined);
                  setEus002Error(false);
                  setCredentials(old => ({...old, password}))
                }}
                onSecretCodeChange={() => {}}
                showResetButton={eus002Error}
                onReset2FA={eus002Error ? () => {
                  return reset2FA().then(() => {
                    setNo2FARegistration(false);
                    setEus002Error(false);
                    setErrorSecretCode(undefined);
                    setStep(0);
                  }).catch(e => {
                    setErrorSecretCode("Ошибка при сбросе кода");
                  });
                } : undefined}
                onSubmit={handlePasswordSubmit}
            />
        )}

        {step === 2 && twoFAEnabled && (
            <PasswordForm
                error={errorSecretCode}
                errorSecretCode={errorSecretCode}
                value=""
                email={username}
                twoFA={true}
                secretCode={secretCode}
                onChange={() => {}}
                onSecretCodeChange={code => {
                  setErrorSecretCode(undefined);
                  setCredentials(old => ({...old, secretCode: code || ''}))
                }}
                onReset2FA={() => {
                  return reset2FA().then(() => {
                    setErrorSecretCode(undefined);
                  }).catch(e => {
                    setErrorSecretCode("Ошибка при сбросе кода");
                  });
                }}
                onSubmit={handleLogin}
            />
        )}
      </Paper>
            )}
          </>
        )}
      </Stack>
    </AuthStepContext.Provider>
  )
}

export default App;
